/**
 * Fabrique des blocs CMS avancé de la categorie "catalogue" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { BreadcrumbPreview } from "@/components/cms-puck/previews/BreadcrumbPreview";
import { CatalogueDecoupagePreview } from "@/components/cms-puck/previews/CatalogueDecoupagePreview";
import { CatalogueDecoupagesPreview } from "@/components/cms-puck/previews/CatalogueDecoupagesPreview";
import { CatalogueDecoupagesFiltersPreview } from "@/components/cms-puck/previews/CatalogueDecoupagesFiltersPreview";
import { CatalogueItemDetailPreview } from "@/components/cms-puck/previews/CatalogueItemDetailPreview";
import { CatalogueListingPreview } from "@/components/cms-puck/previews/CatalogueListingPreview";
import { CatalogueSearchResultsPreview } from "@/components/cms-puck/previews/CatalogueSearchResultsPreview";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { DecoupagePickerField } from "@/components/cms-puck/fields/DecoupagePickerField";
import { CatalogueListingPreviewDecoupageField } from "@/components/cms-puck/fields/CatalogueListingPreviewDecoupageField";
import { CatalogueItemPreviewField } from "@/components/cms-puck/fields/CatalogueItemPreviewField";
import { CatalogueFiltersPartsField } from "@/components/cms-puck/fields/CatalogueFiltersPartsField";
import { CatalogueDecoupagesField } from "@/components/cms-puck/fields/CatalogueDecoupagesField";
import { ButtonAppearanceField } from "@/components/cms-puck/fields/ButtonAppearanceField";
import { ButtonBlock } from "@/components/cms-puck/blocks/button-blocks";
import { normalizeCatalogueDecoupageListItems } from "@/lib/cms-puck/blocks/catalogue-decoupages";
import {
  DEFAULT_CATALOGUE_FILTERS_PARTS,
  type CatalogueFiltersParts,
} from "@/lib/cms-puck/blocks/catalogue-filters";
import {
  catalogSystemLinkForTarget,
  DEFAULT_BUTTON_APPEARANCE,
  type ButtonAppearance,
  type ButtonLink,
} from "@/lib/cms-puck/blocks/button";
import { CMS_ICON_NAMES } from "@/lib/cms-puck/blocks/icons";
import { type SelectionItem } from "@/lib/cms-puck/blocks/selection";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { type CustomField } from "@measured/puck";
import { type RawComponentsConfig } from "../types";
import { useCatalogFlowPreview } from "@/context/cms-puck/CatalogFlowPreviewContext";

/** Listing canvas : cible héritée du binding parcours (œuvres / multiples). */
function CatalogueListingWithFlowPreview({
  columns,
  showRegistrationNumber,
  previewDecoupageId,
}: {
  columns?: number;
  showRegistrationNumber?: boolean;
  previewDecoupageId?: string | null;
}) {
  const { target } = useCatalogFlowPreview();
  return (
    <CatalogueListingPreview
      target={target ?? "ARTWORKS"}
      columns={columns}
      showRegistrationNumber={showRegistrationNumber}
      previewDecoupageId={previewDecoupageId}
      showFilters={false}
    />
  );
}

export function buildCatalogueBlocks({
  contentColumnCountField,
}: {
  contentColumnCountField: CustomField<2 | 3 | 4>;
}) {
  return {
    Breadcrumb: {
      label: cmsNew("cms.new.blocks.breadcrumb.label", "Fil d'Ariane"),
      fields: {
        target: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.cible_catalogue",
            "Cible catalogue",
          ),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.auto_contexte_page",
                "Auto (contexte page)",
              ),
              value: "AUTO",
            },
            {
              label: cmsNew("cms.new.auto.label.uvres", "Œuvres"),
              value: "ARTWORKS",
            },
            {
              label: cmsNew("cms.new.auto.label.multiples", "Multiples"),
              value: "MULTIPRINTS",
            },
          ],
        },
        detailItemTitle: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.sur_fiche_detail",
            "Sur fiche détail",
          ),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.titre_de_l_uvre_multiple",
                "Titre de l'œuvre/multiple",
              ),
              value: "auto",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.segments_url_uniquement",
                "Segments URL uniquement",
              ),
              value: "none",
            },
          ],
        },
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.comportement", "Comportement"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.breadcrumb",
                "Reprend la logique du fil d'Ariane catalogue existant (accueil, découpages, segments d'URL, recherches). Placez ce bloc en haut des templates listing ; sur les fiches détail, le composant natif peut déjà en afficher un — évitez les doublons.",
              )}
            </p>
          ),
        },
      },
      defaultProps: {
        target: "AUTO",
        detailItemTitle: "auto",
        info: null,
      },
      render: ({ detailItemTitle }) => (
        <BreadcrumbPreview detailItemTitle={detailItemTitle} />
      ),
    },

    CatalogueDecoupages: {
      label: cmsNew(
        "cms.new.blocks.catalogueDecoupages.label",
        "Découpages",
      ),
      // Éditeur custom unique — props gérées dans CatalogueDecoupagesField.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fields: {
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.decoupages", "Découpages"),
          render: () => <CatalogueDecoupagesField />,
        },
      } as any,
      defaultProps: {
        info: null,
        target: "ARTWORKS",
        columns: 4,
        showSectionName: true,
        nameAlign: "center",
        items: [],
      },
      render: ({
        target,
        columns,
        showSectionName,
        nameAlign,
        items,
        ...rest
      }) => (
        <CatalogueDecoupagesPreview
          target={target}
          columns={columns}
          showSectionName={showSectionName}
          nameAlign={nameAlign}
          items={normalizeCatalogueDecoupageListItems(
            items,
            target === "MULTIPRINTS" ? "MULTIPRINTS" : "ARTWORKS",
          )}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    CatalogueDecoupage: {
      label: cmsNew("cms.new.blocks.catalogueDecoupage.label", "Découpage"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.decoupages", "Découpages"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.catalogueDecoupage.pre",
                "Gérez les découpages dans l'onglet",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/decoupages", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.decoupages", "Découpages")}
              </button>
              {cmsNew(
                "cms.new.hint.catalogueDecoupage.post",
                ". Placez ce bloc sur n'importe quelle page — le lien pointe vers",
              )}{" "}
              <code className="text-[10px]">
                {cmsNew(
                  "cms.new.auto.text.cette_page_decoupage",
                  "/[cette-page]/[découpage]",
                )}
              </code>
              .
            </p>
          ),
        },
        decoupage: {
          type: "custom",
          label: cmsNew(
            "cms.new.blocks.catalogueDecoupage.label",
            "Découpage",
          ),
          render: ({ value, onChange }) => (
            <DecoupagePickerField
              value={
                value as
                  | {
                      target: "ARTWORKS" | "MULTIPRINTS";
                      decoupageId: string | null;
                    }
                  | undefined
              }
              onChange={onChange}
            />
          ),
        },
        showSectionName: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.afficher_le_nom_de_la_section",
            "Afficher le nom de la section",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
        nameAlign: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.alignement_du_nom",
            "Alignement du nom",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
      },
      defaultProps: {
        info: null,
        decoupage: { target: "ARTWORKS", decoupageId: null },
        showSectionName: true,
        nameAlign: "center",
      },
      render: ({ decoupage, showSectionName, nameAlign, ...rest }) => (
        <CatalogueDecoupagePreview
          decoupage={decoupage}
          showSectionName={showSectionName}
          nameAlign={nameAlign}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    CatalogueFilters: {
      label: cmsNew(
        "cms.new.auto.label.filtres_catalogue",
        "Filtres catalogue",
      ),
      fields: {
        target: {
          type: "select",
          label: cmsNew("cms.new.auto.label.cible", "Cible"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.uvres", "Œuvres"),
              value: "ARTWORKS",
            },
            {
              label: cmsNew("cms.new.auto.label.multiples", "Multiples"),
              value: "MULTIPRINTS",
            },
          ],
        },
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.comportement", "Comportement"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.catalogueFilters.pre",
                "Les recherches redirigent vers",
              )}{" "}
              <code className="text-[10px]">/[cette-page]/search?…</code>{" "}
              {cmsNew(
                "cms.new.hint.catalogueFilters.post",
                "sur le site publié.",
              )}
            </p>
          ),
        },
        parts: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.zones_filtres",
            "Zones (selects, recherche, bouton)",
          ),
          render: ({ value, onChange }) => (
            <CatalogueFiltersPartsField
              value={value as CatalogueFiltersParts | undefined}
              onChange={onChange}
            />
          ),
        },
      },
      defaultProps: {
        target: "ARTWORKS",
        info: null,
        parts: DEFAULT_CATALOGUE_FILTERS_PARTS,
      },
      render: ({ target, ...rest }) => (
        <div className="w-full">
          <CatalogueDecoupagesFiltersPreview
            target={target}
            parts={(rest as { parts?: CatalogueFiltersParts }).parts}
          />
        </div>
      ),
    },

    CatalogueSearchResults: {
      label: cmsNew(
        "cms.new.auto.label.resultats_de_recherche",
        "Résultats de recherche",
      ),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.modele_recherche",
            "Modèle recherche",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.placeBlockOnPageAssignedAs",
                "Placez ce bloc sur la page CMS avancé assignée comme",
              )}{" "}
              <strong>
                {cmsNew(
                  "cms.new.auto.text.resultats_de_recherche",
                  "résultats de recherche",
                )}
              </strong>{" "}
              {cmsNew("cms.new.hint.in", "dans")}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/recherche", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.searchFlow", "Parcours recherche")}
              </button>
              {cmsNew(
                "cms.new.hint.catalogueSearchResults.urlPre",
                ". Les résultats sont déterminés par l'URL (",
              )}
              <code className="text-[10px]">?query=…</code>
              {cmsNew("cms.new.hint.catalogueSearchResults.urlPost", ").")}
            </p>
          ),
        },
        target: {
          type: "select",
          label: cmsNew("cms.new.auto.label.cible", "Cible"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.uvres", "Œuvres"),
              value: "ARTWORKS",
            },
            {
              label: cmsNew("cms.new.auto.label.multiples", "Multiples"),
              value: "MULTIPRINTS",
            },
          ],
        },
        showFilters: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.afficher_les_filtres",
            "Afficher les filtres",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
        columns: contentColumnCountField,
        showRegistrationNumber: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.afficher_le_n_denregistrement",
            "Afficher le n° d'enregistrement",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
      },
      defaultProps: {
        info: null,
        target: "ARTWORKS",
        showFilters: true,
        columns: 4,
        showRegistrationNumber: false,
      },
      render: ({ target, showFilters, columns, showRegistrationNumber }) => (
        <CatalogueSearchResultsPreview
          target={target}
          showFilters={showFilters}
          columns={columns}
          showRegistrationNumber={showRegistrationNumber}
        />
      ),
    },

    CatalogueListing: {
      label: cmsNew(
        "cms.new.auto.label.listing_dun_decoupage",
        "Listing d'un découpage",
      ),
      fields: {
        previewDecoupageId: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.apercu_decoupage",
            "Aperçu découpage",
          ),
          render: ({ value, onChange }) => (
            <CatalogueListingPreviewDecoupageField
              value={value as string | null | undefined}
              onChange={onChange}
            />
          ),
        },
        columns: contentColumnCountField,
        showRegistrationNumber: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.afficher_le_n_denregistrement",
            "Afficher le n° d'enregistrement",
          ),
          options: [
            { label: cmsNew("cms.new.auto.label.oui", "Oui"), value: true },
            { label: cmsNew("cms.new.auto.label.non", "Non"), value: false },
          ],
        },
      },
      defaultProps: {
        previewDecoupageId: null,
        columns: 4,
        showRegistrationNumber: false,
      },
      render: ({ columns, showRegistrationNumber, previewDecoupageId }) => (
        <CatalogueListingWithFlowPreview
          columns={columns}
          showRegistrationNumber={showRegistrationNumber}
          previewDecoupageId={previewDecoupageId as string | null | undefined}
        />
      ),
    },

    CatalogueItemDetail: {
      label: cmsNew(
        "cms.new.auto.label.fiche_uvre_multiple",
        "Fiche œuvre / multiple",
      ),
      fields: {
        templateInfo: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.modele_catalogue",
            "Modèle catalogue",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.catalogueItemDetail.pre",
                "Assignez cette page comme modèle « fiche » dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/catalogue", "_blank")
                }
              >
                {cmsNew(
                  "cms.new.hint.link.catalogFlow",
                  "Parcours catalogue",
                )}
              </button>
              {cmsNew(
                "cms.new.hint.catalogueItemDetail.post",
                ". Sur le site, l'œuvre affichée vient de l'URL (le sélecteur ci-dessous sert uniquement à l'aperçu éditeur).",
              )}
            </p>
          ),
        },
        item: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.apercu_uvre_ou_multiple",
            "Aperçu œuvre / multiple",
          ),
          render: ({ value, onChange }) => (
            <CatalogueItemPreviewField
              value={value as SelectionItem | null | undefined}
              onChange={onChange}
            />
          ),
        },
      },
      defaultProps: { templateInfo: null, item: null },
      render: ({ item }) => <CatalogueItemDetailPreview item={item} />,
    },

    CatalogueCtaButton: {
      label: cmsNew(
        "cms.new.blocks.catalogueCtaButton.label",
        "Bouton catalogue",
      ),
      fields: {
        label: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_traduisible",
            "Libellé (traduisible)",
          ),
          contentEditable: true,
        },
        target: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.cible_catalogue",
            "Cible catalogue",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.uvres", "Œuvres"),
              value: "ARTWORKS",
            },
            {
              label: cmsNew("cms.new.auto.label.multiples", "Multiples"),
              value: "MULTIPRINTS",
            },
          ],
        },
        variant: {
          type: "select",
          label: cmsNew("cms.new.auto.label.style_de_base", "Style de base"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.principal", "Principal"),
              value: "primary",
            },
            {
              label: cmsNew("cms.new.auto.label.secondaire", "Secondaire"),
              value: "secondary",
            },
            {
              label: cmsNew("cms.new.auto.label.contour", "Contour"),
              value: "outline",
            },
          ],
        },
        iconName: {
          type: "select",
          label: cmsNew("cms.new.blocks.icon.label", "Icône"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.aucune", "Aucune"),
              value: "",
            },
            ...CMS_ICON_NAMES.map((n) => ({ label: n, value: n })),
          ],
        },
        iconPosition: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.position_de_licone",
            "Position de l'icône",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.avant", "Avant"),
              value: "before",
            },
            {
              label: cmsNew("cms.new.auto.label.apres", "Après"),
              value: "after",
            },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        appearance: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.au_survol", "Au survol"),
          render: ({ value, onChange }) => (
            <ButtonAppearanceField
              value={value as ButtonAppearance | undefined}
              onChange={onChange}
              sections={["hover"]}
            />
          ),
        },
        // `link` dérivé de `target` via resolveData — champ technique requis par le type.
        link: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.lien_catalogue",
            "Lien catalogue",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.catalogueCtaButton.linkAuto",
                "Le lien pointe automatiquement vers la page Découpages du parcours catalogue (œuvres ou multiples).",
              )}
            </p>
          ),
        },
      },
      defaultProps: {
        label: cmsNew(
          "cms.new.blocks.catalogueCtaButton.defaultLabel",
          "Voir le catalogue",
        ),
        target: "ARTWORKS" as const,
        link: catalogSystemLinkForTarget("ARTWORKS"),
        variant: "primary" as const,
        align: "left" as const,
        iconName: "",
        iconPosition: "before" as const,
        appearance: DEFAULT_BUTTON_APPEARANCE,
      },
      resolveData: ({ props }) => {
        const target =
          props.target === "MULTIPRINTS" ? "MULTIPRINTS" : "ARTWORKS";
        return {
          props: {
            ...props,
            target,
            link: catalogSystemLinkForTarget(target),
          },
        };
      },
      render: ({
        label,
        variant,
        align,
        iconName,
        iconPosition,
        ...rest
      }) => {
        const r = rest as {
          id?: string;
          appearance?: ButtonAppearance;
          style?: BlockStyle;
          link?: ButtonLink;
        };
        return (
          <ButtonBlock
            id={r.id}
            label={label}
            variant={variant}
            align={align}
            iconName={iconName || null}
            iconPosition={iconPosition}
            appearance={r.appearance}
            style={r.style}
          />
        );
      },
    },
  } satisfies Pick<
    RawComponentsConfig,
    | "Breadcrumb"
    | "CatalogueDecoupages"
    | "CatalogueDecoupage"
    | "CatalogueFilters"
    | "CatalogueSearchResults"
    | "CatalogueListing"
    | "CatalogueItemDetail"
    | "CatalogueCtaButton"
  >;
}
